import { createSlice } from '@reduxjs/toolkit';
import { apiGet, apiPost, apiDelete } from 'src/lib/api/axios';
import { toast } from 'src/components/ui/toast';
import { parseFileUploadError } from 'src/lib/api/fileUploadErrors';
import { getTenantFromPath, prefixWithTenant, resolveTenantCode } from 'src/lib/tenantRouting';

/** Client-only: persist tenant context from URL or prior session (not from tenants[] order). */
function mergeActiveTenantCodeIntoProfile(profileData) {
  const data = { ...profileData };
  const tenantCodes = new Set((data.tenants || []).map((t) => t.code));
  let active = null;
  if (typeof window !== 'undefined') {
    const fromPath = getTenantFromPath(window.location.pathname);
    if (fromPath && tenantCodes.has(fromPath)) {
      active = fromPath;
    }
  }
  if (!active && typeof window !== 'undefined') {
    try {
      const prevRaw = localStorage.getItem('user');
      if (prevRaw) {
        const prev = JSON.parse(prevRaw);
        const prevActive = prev?.active_tenant_code;
        if (prevActive && tenantCodes.has(prevActive)) {
          active = prevActive;
        }
      }
    } catch {
      /* ignore */
    }
  }
  if (active) {
    data.active_tenant_code = active;
  } else {
    delete data.active_tenant_code;
  }
  return data;
}

function getTenantScopedAccountPath(path, getState, tenantCode) {
  const profileTenants = getState?.()?.profile?.profileData?.tenants;
  const resolvedTenantCode =
    tenantCode ?? (typeof window !== 'undefined' ? resolveTenantCode(window.location.pathname, profileTenants) : null);
  return resolvedTenantCode ? prefixWithTenant(path, resolvedTenantCode) : path;
}

// ----------------------------------------------------------------------
// Available in shared Redux files as needed by JWTContext code

const initialState = {
  isProfileLoading: false,
  isEditMode: false,
  profileData: null,
  isLoading: false,
  profileImg: null,
  error: ''
};

const slice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    // start loading
    startProfileLoading(state) {
      state.isProfileLoading = true;
    },
    // Edit btn
    handleEditProfile(state, action) {
      state.isEditMode = action.payload;
    },
    // GET profile
    getProfileSuccess(state, action) {
      state.isProfileLoading = false;
      state.profileData = action.payload;
    },
    // GET profile
    getProfileImgSuccess(state, action) {
      state.isProfileLoading = false;
      state.profileImg = action.payload;
    },
    // HAS ERROR
    hasError(state, action) {
      state.isProfileLoading = false;
      state.isLoading = false;
      state.error = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { handleEditProfile } = slice.actions;

// ----------------------------------------------------------------------

export function UpdateProfile(body) {
  return async (dispatch) => {
    dispatch(slice.actions.startProfileLoading());
    try {
      // Only send fields accepted by the backend schema
      const allowedKeys = ['firstname', 'surname', 'areas_of_interest', 'job_title', 'agreement', 'marketing_opt_in'];
      const payload = Object.fromEntries(
        Object.entries(body || {}).filter(([key, value]) => allowedKeys.includes(key) && value !== undefined)
      );

      const response = await apiPost('account/update', payload, {
        meta: {
          globalError: false, // User action, handle locally
          context: 'profile-update'
        }
      });
      const merged = mergeActiveTenantCodeIntoProfile(response.data);
      dispatch(slice.actions.getProfileSuccess(merged));
      localStorage.setItem('user', JSON.stringify(merged));
      return { success: true, data: merged };
    } catch {
      const errorMsg = 'There was an error updating your profile. Please try again later.';
      dispatch(slice.actions.hasError(errorMsg));
      // No toast here because we want for the page to handle the error as part of the form!
      return { success: false, error: errorMsg };
    }
  };
}

export function DeleteProfileImg() {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startProfileLoading());
    try {
      await apiDelete(getTenantScopedAccountPath('/account/delete/image', getState), {
        meta: {
          globalError: false, // User action, handle locally
          context: 'profile-image-deletion'
        }
      });
      dispatch(slice.actions.getProfileImgSuccess(null));
    } catch {
      const errorMsg = 'There was an error deleting your profile image. Please try again later.';
      dispatch(slice.actions.hasError(errorMsg));
      toast.error(errorMsg);
    }
  };
}

// ----------------------------------------------------------------------

export function GetProfile() {
  return async (dispatch) => {
    dispatch(slice.actions.startProfileLoading());
    try {
      const response = await apiGet('account/info', {
        meta: {
          globalError: true, // Critical for app functionality
          context: 'profile-fetch',
          description: 'We failed to fetch your profile. Please contact support if the problem persists.',
          title: 'Failed to fetch profile',
          actions: [
            {
              label: 'Try Again',
              onClick: () => dispatch(GetProfile()),
              variant: 'primary'
            }
          ]
        }
      });
      const merged = mergeActiveTenantCodeIntoProfile(response.data);
      dispatch(slice.actions.getProfileSuccess(merged));
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    } catch (error) {
      dispatch(slice.actions.hasError(error.parsed?.description || 'Failed to fetch profile'));
      return null; // Return null on error
    }
  };
}

// ----------------------------------------------------------------------

export function UploadrofileImg(body) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startProfileLoading());
    try {
      const formData = new FormData();
      formData.append('file', body.file);
      await apiPost(getTenantScopedAccountPath('/account/upload/image', getState), formData, {
        meta: {
          globalError: false, // User action, handle locally
          context: 'profile-image-upload'
        }
      });
      dispatch(slice.actions.getProfileImgSuccess(URL.createObjectURL(body.file)));
    } catch (error) {
      // Always clear loading state on error
      dispatch(slice.actions.hasError(''));

      const status = error?.response?.status || error?.parsed?.code;

      // Handle 413 (File Too Large) and 415 (Unsupported Media Type) errors
      if (status === 413 || status === 415) {
        const rawMessage =
          error?.parsed?.description ||
          error?.response?.data?.detail ||
          (status === 415
            ? 'This file type is not supported. Please upload a JPEG or PNG image.'
            : 'Image is too large. Please upload an image smaller than 5 MB.');
        const message = parseFileUploadError(rawMessage);
        toast.error(message);
      } else {
        const errorMsg = 'There was an error uploading your profile image. Please try again later.';
        dispatch(slice.actions.hasError(errorMsg));
        toast.error(errorMsg);
      }
    }
  };
}

// ----------------------------------------------------------------------

export function GetProfileImg({ suppressError = false, tenantCode } = {}) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startProfileLoading());
    try {
      const response = await apiGet(getTenantScopedAccountPath('/account/image', getState, tenantCode), {
        meta: {
          globalError: false, // Not critical, handle locally
          context: 'profile-image-fetch'
        }
      });

      if (response.status === 204) {
        // No image was found, but this is expected behaviour for most users
        dispatch(slice.actions.getProfileImgSuccess(null));
        return;
      }
      dispatch(slice.actions.getProfileImgSuccess(response?.data?.url));
      return response;
    } catch {
      const errorMsg = 'There was an error fetching your profile image. Please try again later.';
      if (!suppressError) {
        dispatch(slice.actions.hasError(errorMsg));
        toast.error(errorMsg);
      }
    }
  };
}

// ----------------------------------------------------------------------

export function SaveRegistrationData({ role, fmTypes, interests, quizAnswers }) {
  return async (dispatch) => {
    try {
      const payload = {
        role,
        fm_types: fmTypes,
        interests,
        // Map quiz answers to individual frequency fields
        quiz_searching_frequency: quizAnswers?.searching ?? null,
        quiz_reviewing_frequency: quizAnswers?.reviewing ?? null,
        quiz_writing_frequency: quizAnswers?.writing ?? null
      };

      await apiPost('account/registration', payload, {
        meta: {
          globalError: false,
          context: 'save-registration-data'
        }
      });

      // Refresh profile to get updated registration data
      await dispatch(GetProfile());
      return { success: true };
    } catch {
      const errorMsg = 'There was an error saving your preferences.';
      dispatch(slice.actions.hasError(errorMsg));
      return { success: false, error: errorMsg };
    }
  };
}
